import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BiSearchAlt } from 'react-icons/bi';
import { FiRotateCcw } from 'react-icons/fi';
import { TiDownload } from 'react-icons/ti';

import Button from '../../../../components/Button';
import DateInput from '../../../../components/DateInput';
import Input from '../../../../components/Input';
import PageTitle from '../../../../components/PageTitle';
import Select from '../../../../components/Select';
import { useToast } from '../../../../hooks/toast';
import api from '../../../../services/api';

import { normalizeAlertHistoryDateParam } from './dateParams';
import * as S from './styles';

interface IAlertHistoryRecord {
  id: string;
  type: 'sensor' | 'battery';
  device_name: string;
  sensor_name?: string;
  message: string;
  read: boolean;
  timestamp: string;
}

interface IFiltersFormData {
  start_date?: string;
  end_date?: string;
  type?: 'sensor' | 'battery' | 'all';
  device_name?: string;
  sensor_name?: string;
  read?: 'true' | 'false' | 'all';
}

const typeOptions = [
  { id: 'all', name: 'Todos os alertas' },
  { id: 'sensor', name: 'Alertas de sensor' },
  { id: 'battery', name: 'Alertas de bateria' },
];

const readOptions = [
  { id: 'all', name: 'Todos os status' },
  { id: 'true', name: 'Somente lidos' },
  { id: 'false', name: 'Somente não lidos' },
];

const pageSizeOptions = [10, 20, 50];
const defaultFilters: IFiltersFormData = { type: 'all', read: 'all' };

const sanitizeText = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();

  return trimmed.length ? trimmed : undefined;
};

const AlertsHistory: React.FC = () => {
  const filterFormRef = useRef<FormHandles>(null);
  const filtersRef = useRef<IFiltersFormData | undefined>(defaultFilters);
  const { addToast } = useToast();
  const [alerts, setAlerts] = useState<IAlertHistoryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [markingIds, setMarkingIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(pageSizeOptions[1]);
  const [total, setTotal] = useState(0);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    [],
  );

  const buildAlertParams = useCallback(
    (
      filters?: IFiltersFormData,
      includePagination?: boolean,
      overridePage?: number,
      overrideLimit?: number,
      dateFormatter?: (value?: string) => string | undefined,
    ) => {
      const params: Record<string, string> = {};

      if (includePagination) {
        const currentPage = overridePage ?? page;
        const currentLimit = overrideLimit ?? limit;

        params.page = String(currentPage);
        params.limit = String(currentLimit);
      }

      const typeFilter = sanitizeText(filters?.type);
      if (typeFilter && (typeFilter === 'sensor' || typeFilter === 'battery' || typeFilter === 'all')) {
        params.type = typeFilter;
      }

      const deviceName = sanitizeText(filters?.device_name);
      if (deviceName) {
        params.device_name = deviceName;
      }

      const sensorName = sanitizeText(filters?.sensor_name);
      if (sensorName) {
        params.sensor_name = sensorName;
      }

      const readFilter = sanitizeText(filters?.read);
      if (readFilter === 'true' || readFilter === 'false') {
        params.read = readFilter;
      }

      const formatDate = dateFormatter || normalizeAlertHistoryDateParam;
      const startDate = formatDate(filters?.start_date);
      const endDate = formatDate(filters?.end_date);

      if (startDate) {
        params.start_date = startDate;
      }

      if (endDate) {
        params.end_date = endDate;
      }

      return params;
    },
    [page, limit],
  );

  const fetchAlerts = useCallback(
    async (filters?: IFiltersFormData, overridePage?: number, overrideLimit?: number) => {
      try {
        setIsLoading(true);

        const params = buildAlertParams(filters, true, overridePage, overrideLimit);

        type AlertsHistoryResponse = {
          data?: IAlertHistoryRecord[];
          alerts?: IAlertHistoryRecord[];
          results?: IAlertHistoryRecord[];
          items?: IAlertHistoryRecord[];
          total?: number;
          total_count?: number;
          count?: number;
          pagination?: { total?: number; count?: number };
          meta?: { total?: number; count?: number };
        } | IAlertHistoryRecord[];

        const { data } = await api.get<AlertsHistoryResponse>('/alerts-history', { params });

        let records: IAlertHistoryRecord[] = [];
        let totalFromResponse: number | undefined;

        if (Array.isArray(data)) {
          records = data;
          totalFromResponse = data.length;
        } else {
          records =
            (Array.isArray(data.data) && data.data) ||
            (Array.isArray(data.alerts) && data.alerts) ||
            (Array.isArray(data.results) && data.results) ||
            (Array.isArray(data.items) && data.items) ||
            [];

          totalFromResponse =
            data.total ??
            data.total_count ??
            data.count ??
            data.pagination?.total ??
            data.pagination?.count ??
            data.meta?.total ??
            data.meta?.count ??
            undefined;
        }

        setAlerts(records);
        setTotal(totalFromResponse ?? records.length);
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro ao buscar alertas',
          description: 'Não foi possível carregar o histórico de alertas. Tente novamente.',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [addToast, buildAlertParams],
  );

  const handleExportCsv = useCallback(async () => {
    try {
      setIsExporting(true);

      const params = buildAlertParams(filtersRef.current, false);
      const response = await api.post('/alerts-history/csv', undefined, {
        params,
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      const fileURL = window.URL.createObjectURL(blob);
      const fileName = response.headers['x-filename'] || 'alertas.csv';

      link.href = fileURL;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      addToast({
        type: 'success',
        title: 'CSV exportado',
        description: 'Arquivo CSV exportado com sucesso.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao exportar CSV',
        description: 'Nao foi possivel exportar os alertas. Tente novamente.',
      });
    } finally {
      setIsExporting(false);
    }
  }, [addToast, buildAlertParams]);

  const handleSubmit = useCallback(
    (formData: IFiltersFormData) => {
      const normalizedFilters = { ...defaultFilters, ...formData };
      filtersRef.current = normalizedFilters;
      setPage(1);
      fetchAlerts(normalizedFilters, 1, limit);
    },
    [fetchAlerts, limit],
  );

  const handleClearFilters = useCallback(() => {
    filterFormRef.current?.reset();
    filterFormRef.current?.setData(defaultFilters);
    filtersRef.current = defaultFilters;
    setPage(1);
    fetchAlerts(defaultFilters, 1, limit);
  }, [fetchAlerts, limit]);

  useEffect(() => {
    fetchAlerts(filtersRef.current, page, limit);
  }, [fetchAlerts, page, limit]);

  useEffect(() => {
    // Foca no campo de pesquisa quando o componente é montado
    const searchInput = filterFormRef.current?.getFieldRef('device_name');
    if (searchInput) {
      searchInput.focus();
    }
  }, []);

  const totalPages = useMemo(() => {
    if (!total) {
      return 1;
    }

    return Math.max(1, Math.ceil(total / limit));
  }, [total, limit]);

  const canGoBack = page > 1;
  const canGoForward = page < totalPages;

  const handlePreviousPage = useCallback(() => {
    if (!canGoBack) {
      return;
    }

    setPage(oldPage => Math.max(1, oldPage - 1));
  }, [canGoBack]);

  const handleNextPage = useCallback(() => {
    if (!canGoForward) {
      return;
    }

    setPage(oldPage => oldPage + 1);
  }, [canGoForward]);

  const handlePageSizeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(event.target.value) || pageSizeOptions[1];

    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleMarkAsRead = useCallback(
    async (alert: IAlertHistoryRecord) => {
      if (alert.read) {
        return;
      }

      setMarkingIds(prev => (prev.includes(alert.id) ? prev : [...prev, alert.id]));

      try {
        if (alert.type === 'sensor') {
          await api.put('/alerts/read', {
            alerts_id: [alert.id],
          });
        } else {
          await api.put('/battery/alerts/read', {
            alerts_id: [alert.id],
          });
        }

        setAlerts(prevAlerts =>
          prevAlerts.map(item => (item.id === alert.id ? { ...item, read: true } : item)),
        );
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro ao marcar alerta',
          description: 'Não foi possível marcar o alerta como lido. Tente novamente.',
        });
      } finally {
        setMarkingIds(prev => prev.filter(id => id !== alert.id));
      }
    },
    [addToast],
  );

  const showingFrom = total === 0 ? 0 : (page - 1) * limit + 1;
  const showingTo = total === 0 ? 0 : Math.min(total, page * limit);

  return (
    <S.Container>
      <PageTitle title="Alertas" sub="Histórico" />

      <S.FiltersCard>
        <Form ref={filterFormRef} onSubmit={handleSubmit} initialData={defaultFilters}>
          <h3>Filtros</h3>
          <S.FieldsRow>
            <div>
              <p>Data inicial</p>
              <DateInput name="start_date" placeholder="Selecione" />
            </div>
            <div>
              <p>Data final</p>
              <DateInput name="end_date" placeholder="Selecione" />
            </div>
            <div>
              <p>Tipo</p>
              <Select name="type" data={typeOptions} defaultValue="all" showEmptyOption={false} />
            </div>
            <div>
              <p>Status</p>
              <Select name="read" data={readOptions} defaultValue="all" showEmptyOption={false} />
            </div>
            <div>
              <p>Dispositivo</p>
              <Input name="device_name" placeholder="Nome do dispositivo" />
            </div>
            <div>
              <p>Sensor</p>
              <Input name="sensor_name" placeholder="Nome do sensor" />
            </div>
          </S.FieldsRow>

          <S.Actions>
            <button type="button" className="clearButton" onClick={handleClearFilters} disabled={isLoading}>
              <FiRotateCcw size={18} />
              &nbsp;Limpar filtros
            </button>
            <Button type="button" onClick={handleExportCsv} disabled={isLoading || isExporting}>
              <TiDownload size={20} />
              Exportar CSV
            </Button>
            <Button type="submit" disabled={isLoading}>
              <BiSearchAlt size={20} />
              Buscar
            </Button>
          </S.Actions>
        </Form>
      </S.FiltersCard>

      <S.ResultsCard>
        {isLoading ? (
          <S.LoadingState>Carregando alertas...</S.LoadingState>
        ) : alerts.length === 0 ? (
          <S.EmptyState>Nenhum alerta encontrado para o período selecionado.</S.EmptyState>
        ) : (
          <>
            <S.Table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Dispositivo</th>
                  <th>Sensor</th>
                  <th>Mensagem</th>
                  <th>Status</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map(alert => {
                  const isMarking = markingIds.includes(alert.id);

                  return (
                    <tr key={alert.id}>
                      <td>{dateFormatter.format(new Date(alert.timestamp))}</td>
                      <td>
                        <S.TypeTag variant={alert.type}>{alert.type === 'sensor' ? 'Sensor' : 'Bateria'}</S.TypeTag>
                      </td>
                      <td>{alert.device_name || '-'}</td>
                      <td>{alert.sensor_name || '-'}</td>
                      <td>
                        <S.Message>{alert.message}</S.Message>
                      </td>
                      <td>
                        <S.ReadBadge read={alert.read}>{alert.read ? 'Lida' : 'Não lida'}</S.ReadBadge>
                      </td>
                      <td>
                        <S.MarkReadButton
                          type="button"
                          onClick={() => handleMarkAsRead(alert)}
                          disabled={alert.read || isMarking}
                        >
                          {alert.read ? 'Lido' : isMarking ? 'Marcando...' : 'Marcar Lido'}
                        </S.MarkReadButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </S.Table>

            <S.PaginationBar>
              <S.PaginationInfo>
                {`Mostrando ${showingFrom}-${showingTo} de ${total} alertas`}
              </S.PaginationInfo>
              <S.PaginationActions>
                <S.PageSizeControl>
                  Itens por página
                  <S.PageSizeSelect value={limit} onChange={handlePageSizeChange}>
                    {pageSizeOptions.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </S.PageSizeSelect>
                </S.PageSizeControl>
                <div className="nav">
                  <S.PaginationButton type="button" onClick={handlePreviousPage} disabled={!canGoBack || isLoading}>
                    Anterior
                  </S.PaginationButton>
                  <span>{`Página ${page} de ${totalPages}`}</span>
                  <S.PaginationButton type="button" onClick={handleNextPage} disabled={!canGoForward || isLoading}>
                    Próxima
                  </S.PaginationButton>
                </div>
              </S.PaginationActions>
            </S.PaginationBar>
          </>
        )}
      </S.ResultsCard>
    </S.Container>
  );
};

export default AlertsHistory;
